import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tokenStore.getAccess()) {
      setUser(false);
      setLoading(false);
      return;
    }
    const boot = async () => {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data);
      } catch (e) {
        // Access token scaduto: tenta il refresh prima di forzare il re-login.
        const refresh = tokenStore.getRefresh();
        if (refresh) {
          try {
            const { data } = await api.post("/auth/refresh", { refresh_token: refresh });
            tokenStore.set(data.access_token, null);
            const res = await api.get("/auth/me");
            setUser(res.data);
            return;
          } catch (_) {
            /* refresh scaduto: procedi al logout */
          }
        }
        tokenStore.clear();
        setUser(false);
      }
    };
    boot().finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onLogout = () => {
      tokenStore.clear();
      setUser(false);
    };
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  // Refresh proattivo silenzioso: rinnova l'access token prima della scadenza (30 min) mentre l'utente è loggato.
  useEffect(() => {
    if (!user) return undefined;
    const id = setInterval(() => {
      const refresh = tokenStore.getRefresh();
      if (!refresh) return;
      api
        .post("/auth/refresh", { refresh_token: refresh })
        .then(({ data }) => tokenStore.set(data.access_token, null))
        .catch(() => {});
    }, 25 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokenStore.set(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Logout request failed:", e);
    }
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
